import { Clapperboard } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { ExpandableContent } from '@/components/ui/ExpandableContent'
import { MarkdownEditor } from '@/components/ui/markdown-editor'
import { posthogReplayUrl } from '@/lib/posthogLinks'
import { cn } from '@/lib/utils'
import {
  type SalesLeadActivity,
  SalesLeadActivityType,
  type SalesLeadStatus,
  salesLeadStatusColors,
  salesLeadStatusLabels,
} from '@/models/salesLead'
import { ActivityAttachments } from './ActivityAttachments'
import { activityTypeStyles } from './activityTypeStyles'
import { EventNotes } from './EventNotes'
import { FieldUpdateDiff } from './FieldUpdateDiff'
import { PlainTextWithCollapsedQuotes } from './PlainTextWithCollapsedQuotes'

/** An activity's body: a status move, a field diff, an event or its text, then its attachments. */
export function ActivityContent({ activity }: { activity: SalesLeadActivity }) {
  const replayUrl = posthogReplayUrl(activity.posthogSessionId)
  const useMarkdown =
    activity.activityType === SalesLeadActivityType.NOTE ||
    activity.activityType === SalesLeadActivityType.EMAIL_OUTBOUND

  return (
    <>
      {activity.activityType === SalesLeadActivityType.STATUS_CHANGE ? (
        <p className="mt-2 flex flex-wrap items-center gap-1 text-sm">
          Changed status:{' '}
          <span
            className={cn(
              'rounded px-1.5 py-0.5 text-xs font-medium',
              salesLeadStatusColors[activity.oldStatus as SalesLeadStatus]
            )}
          >
            {salesLeadStatusLabels[activity.oldStatus as SalesLeadStatus] || activity.oldStatus}
          </span>
          →
          <span
            className={cn(
              'rounded px-1.5 py-0.5 text-xs font-medium',
              salesLeadStatusColors[activity.newStatus as SalesLeadStatus]
            )}
          >
            {salesLeadStatusLabels[activity.newStatus as SalesLeadStatus] || activity.newStatus}
          </span>
          {activity.notes && <span>— {activity.notes}</span>}
        </p>
      ) : activity.activityType === SalesLeadActivityType.FIELD_UPDATE ? (
        <FieldUpdateDiff notes={activity.notes} />
      ) : activityTypeStyles[activity.activityType].event ? (
        <ExpandableContent>
          <EventNotes notes={activity.notes} />
        </ExpandableContent>
      ) : (
        <ExpandableContent>
          {useMarkdown ? (
            <MarkdownEditor
              value={activity.notes}
              onChange={() => {}}
              readOnly
              className="mt-2 border-none! bg-transparent! p-0!"
              contentEditableClassName="p-0! min-h-0! border-none!"
            />
          ) : (
            <PlainTextWithCollapsedQuotes text={activity.notes} />
          )}
        </ExpandableContent>
      )}
      {replayUrl && (
        <Button variant="link" size="xs" href={replayUrl} target="_blank" className="mt-1 px-0!">
          <Clapperboard />
          Watch session replay
        </Button>
      )}
      <ActivityAttachments attachments={activity.attachments} />
    </>
  )
}
